import type { OperationId, TokenResponse } from '@ctcells/contract'
import {
  canCallOperation,
  TOKEN_REFRESH_RATIO,
  useSessionStore,
  type Permission,
} from '@ctcells/core'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import type { ApiError } from '../errors.ts'
import { configureSessionTeardown, login, refreshSession, restoreSession } from './authApi.ts'
import { logout } from './logout.ts'
import { getAccessExpiresAt, getAccessToken } from './tokenStore.ts'

/**
 * 인증 훅.
 *
 * ⚠ 여기만 `useMutation` 을 직접 쓴다 — 로그인·로그아웃은 **도메인 캐시를 건드리는 명령이 아니고**
 *   세션 전체를 갈아치우는 일이라 `createCommand`(무효화 키) 모형에 맞지 않는다. 대신
 *   성공 시 **캐시를 통째로 비운다** — 전 사용자의 데이터가 남으면 안 된다.
 */

export interface LoginVars {
  username: string
  password: string
  remember?: boolean
}

export function useLogin(): UseMutationResult<TokenResponse, ApiError, LoginVars> {
  const queryClient = useQueryClient()
  return useMutation<TokenResponse, ApiError, LoginVars>({
    mutationFn: (vars) => login(vars),
    onSuccess: () => {
      queryClient.clear()
    },
  })
}

export function useLogout(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, void>({
    mutationFn: () => logout(),
    onSettled: () => {
      queryClient.clear()
    },
  })
}

/**
 * 세션 부트스트랩 — **앱 셸이 한 번 부른다.**
 *
 * 1. `sessionStorage` 에 refresh 토큰이 있으면 조용히 갱신한다. 그동안 상태는 `unknown`(로딩).
 * 2. 세션이 끝나면(명시 로그아웃 · 갱신 실패 · 서버 강제 종료) **쿼리 캐시를 비운다.**
 * 3. TTL 의 80% 지점에 미리 갱신한다.
 *    ⚠ **폴링이 아니다** — 토큰 수명 타이머이지 서버 상태 조회가 아니다(DP-01 무관).
 */
export function useSessionBootstrap(): void {
  const queryClient = useQueryClient()
  const started = useRef(false)

  useEffect(() => {
    configureSessionTeardown(() => {
      queryClient.clear()
    })
    return () => {
      configureSessionTeardown(null)
    }
  }, [queryClient])

  useEffect(() => {
    if (started.current) return
    started.current = true
    void restoreSession()
  }, [])

  /**
   * 만료 전 갱신 타이머.
   *
   * 🔴 **갱신에 성공하면 «다시» 잡는다.** 종전에는 의존이 `[status]` 하나라 첫 갱신 뒤
   *    `status` 가 `authenticated` 그대로여서 **효과가 다시 돌지 않았다** — 타이머가 딱
   *    한 번만 서고, 두 번째 수명부터는 만료될 때까지 아무도 갱신하지 않았다.
   *    (주석은 「토큰이 바뀔 때마다 다시 잡는다」였는데 실물이 그렇지 않았다.)
   * 🔴 **실패하면 다시 잡지 않는다.** 서버가 죽어 있으면 남은 수명이 음수라 최소 대기(1초)로
   *    떨어져 **초당 한 번 재시도**가 된다 — 그것이 곧 폴링이다. 복구는 다음 «실제» 요청이
   *    한다(`client.ts` 의 보내기 전 갱신). 4xx 면 `refreshSession` 이 세션을 끝내 여기도 멈춘다.
   * ⚠ **폴링이 아니다** — 토큰 수명 타이머이지 서버 상태 조회가 아니다(DP-01 무관).
   */
  const status = useSessionStore((state) => state.status)
  useEffect(() => {
    if (status !== 'authenticated') return
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined

    const arm = () => {
      const expiresAt = getAccessExpiresAt()
      if (expiresAt === null || getAccessToken() === null) return
      const lead = Math.max(1000, (expiresAt - Date.now()) * TOKEN_REFRESH_RATIO)
      timer = setTimeout(() => {
        void refreshSession().then((ok) => {
          if (ok && alive) arm()
        })
      }, lead)
    }
    arm()

    return () => {
      alive = false
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [status])
}

/**
 * 화면 노출 판단에 쓰는 현재 권한. 🔴 집행은 서버 403 이다.
 * 🔵 원천은 `GET /auth/me` 의 `operations` 이고, 아직 못 받았으면 역할 표다(`resolvePermissions`).
 */
export function usePermissions(): readonly Permission[] {
  return useSessionStore((state) => state.permissions)
}

export function useCurrentUser() {
  return useSessionStore((state) => state.user)
}

/**
 * 지금 이 브라우저가 쓰는 세션 id — 서버가 준 `TokenResponse.session_id` **그대로**다.
 *
 * 🔴 「이 기기」 판정에만 쓴다. `GET /sessions` 응답에는 그 표시가 없어서(계약 구멍)
 *    이 값과 `Session.session_id` 를 맞춰 보는 것이 유일한 방법이다. 프론트가 다른 축
 *    (마지막 접속 시각 등)으로 추론하지 않는다.
 */
export function useSessionId(): string | null {
  return useSessionStore((state) => state.sessionId)
}

/**
 * `GET /auth/me` 로 받은 **나의 연산 목록**과 판정 원천.
 *
 * - `operations` — `null` 이면 아직 못 받았다(로그인 직후 · 실패). 그때 `source` 는 `'role'`
 * - 부르는 시점은 `auth/me.ts` 가 정한다(로그인 · 부팅 복구 · 세션 바뀜 뒤 1회). 🔴 이 훅은 **요청을 안 쏜다**
 *   — 화면이 이 훅을 여러 번 불러도 요청은 늘지 않는다(폴링 없음 · DP-01).
 */
export function useMe(): { operations: readonly string[] | null; source: 'operations' | 'role' } {
  const operations = useSessionStore((state) => state.operations)
  return { operations, source: operations === null ? 'role' : 'operations' }
}

/**
 * 경로 단위 게이트 — 「이 operationId 를 부를 수 있나」. ⚠ 아직 부르는 화면이 없다(권한 5종 판정만 갈아탔다).
 * 🔴 `operations` 를 모르면 **거짓**(비활성 + 사유)이다. 집행은 서버 403 이다.
 */
export function useCanCall(operationId: OperationId): boolean {
  return useSessionStore((state) => canCallOperation(state.operations, operationId))
}
